import { useState } from "react";
import { Link } from "react-router-dom";
import { authAPI } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { CreditCard, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Backend always returns the same generic ack (anti-enumeration) so we
      // surface the same confirmation regardless of whether the email exists.
      await authAPI.forgotPassword(email.trim());
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="forgot-password-done">
        <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8 text-center">
          <CheckCircle size={40} weight="duotone" className="text-emerald-500 mx-auto" />
          <h1 className="text-lg font-bold mt-3 text-zinc-900">Check your inbox</h1>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
            If an account with <span className="font-medium text-zinc-800">{email}</span> exists, we've sent a reset link to it.
            The link is good for 1 hour.
          </p>
          <Link to="/login" className="inline-block mt-6 text-xs text-[#002FA7] hover:underline" data-testid="back-to-login">
            ← Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="max-w-sm w-full bg-white border border-zinc-200 rounded-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard size={20} weight="duotone" className="text-[#002FA7]" />
          <h1 className="text-xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: "DM Sans, sans-serif" }}>Expenser</h1>
        </div>
        <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-bold mb-6">Reset your password</p>

        <p className="text-sm text-zinc-600 mb-4 leading-relaxed">
          Enter the email tied to your account. If we find a match, we'll send you a link to choose a new password.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Email</Label>
            <div className="relative mt-1">
              <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="rounded-sm pl-8" data-testid="forgot-password-email" autoFocus required />
            </div>
          </div>
          <Button type="submit" disabled={submitting || !email} data-testid="forgot-password-submit"
            className="w-full bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm">
            {submitting ? "Sending..." : "Send reset link"}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-700" data-testid="forgot-cancel">
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
